import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useStartAudio } from '@livekit/components-react';
import { useAuth } from '../context/AuthContext';
import { useWebRTC } from '../context/WebRTCContext';
import { VideoElement } from './PlayerVideoCard';
import ThemedCheckbox from './ThemedCheckbox';
import { getAvatarEmoji } from '../constants/avatars';

export default function MediaSetupScreen({ gameActive, onDone }) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const webrtc = useWebRTC() ?? {};
  const { canPlayAudio, mergedProps: startAudioProps } = useStartAudio({ props: {} });

  const {
    cameraEnabled,
    micEnabled,
    toggleCamera,
    toggleMic,
    localCameraStream,
    audioConstraints,
    changeAudioConstraints,
    krispEnabled,
    krispSupported,
    toggleKrisp,
    selectedMicId,
    selectedCameraId,
    changeMicDevice,
    changeCameraDevice,
    disableAllMedia,
  } = webrtc;

  const [availableMics, setAvailableMics] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);

  useEffect(() => {
    if (!cameraEnabled && !micEnabled) return;
    navigator.mediaDevices?.enumerateDevices().then((devices) => {
      setAvailableMics(devices.filter((d) => d.kind === 'audioinput'));
      setAvailableCameras(devices.filter((d) => d.kind === 'videoinput'));
    }).catch(() => {});
  }, [cameraEnabled, micEnabled]);

  const avatarEmoji = getAvatarEmoji(profile?.avatarKey);
  const hasAV = cameraEnabled || micEnabled;
  const showMicSelector = micEnabled && availableMics.length > 1;
  const showCameraSelector = cameraEnabled && availableCameras.length > 1;

  function unlockAudio() {
    if (!canPlayAudio) startAudioProps.onClick?.();
  }

  async function handleEnableAV() {
    unlockAudio();
    if (!cameraEnabled) await toggleCamera?.();
    if (!micEnabled) await toggleMic?.();
  }

  function handleJoin() {
    unlockAudio();
    onDone();
  }

  async function handleJoinWithoutAudio() {
    unlockAudio();
    await disableAllMedia?.();
    onDone();
  }

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 backdrop-blur-sm bg-black/55"
      role="dialog"
      aria-modal="true"
      aria-label={t('mediaSetup.title')}
    >
      <div className="t-bg-surface t-border border rounded-2xl w-full max-w-sm shadow-2xl flex flex-col max-h-[90dvh]">
        {/* Scrollable content */}
        <div className="overflow-y-auto p-6 space-y-5 min-h-0">
          {/* Header */}
          <div>
            <h2 className="text-xl font-bold t-text">{t('mediaSetup.title')}</h2>
            <p className="text-sm t-text-muted mt-1">{t('mediaSetup.subtitle')}</p>
          </div>

          {/* Video preview */}
          <div className="aspect-video rounded-xl overflow-hidden t-bg-input relative">
            {localCameraStream && cameraEnabled ? (
              <VideoElement stream={localCameraStream} muted />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span className="text-5xl select-none" aria-hidden="true">{avatarEmoji}</span>
                <span className="text-xs t-text-muted">{profile?.username}</span>
              </div>
            )}
          </div>

          {/* Enable AV button — shown when neither camera nor mic is on */}
          {!hasAV && (
            <button
              type="button"
              onClick={handleEnableAV}
              className="w-full t-btn-primary font-semibold rounded-lg px-4 py-3 t-focus-ring"
            >
              {t('mediaSetup.enableAV')}
            </button>
          )}

          {/* Individual recovery buttons — shown when only one device enabled */}
          {hasAV && (!cameraEnabled || !micEnabled) && (
            <div className="flex gap-2">
              {!cameraEnabled && (
                <button
                  type="button"
                  onClick={() => toggleCamera?.()}
                  className="flex-1 t-btn-primary font-semibold rounded-lg px-3 py-2.5 text-sm t-focus-ring"
                >
                  {t('mediaSetup.enableCamera')}
                </button>
              )}
              {!micEnabled && (
                <button
                  type="button"
                  onClick={() => toggleMic?.()}
                  className="flex-1 t-btn-primary font-semibold rounded-lg px-3 py-2.5 text-sm t-focus-ring"
                >
                  {t('mediaSetup.enableMic')}
                </button>
              )}
            </div>
          )}

          {/* Device selectors — shown after permission granted */}
          {hasAV && (showMicSelector || showCameraSelector) && (
            <div className="space-y-3">
              {showMicSelector && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium t-text-secondary block" htmlFor="setup-mic-select">
                    {t('players.selectMic')}
                  </label>
                  <select
                    id="setup-mic-select"
                    value={selectedMicId ?? ''}
                    onChange={(e) => changeMicDevice?.(e.target.value || null)}
                    className="w-full t-bg-input t-text t-border border rounded-lg px-3 py-2 text-sm t-focus-ring"
                  >
                    {availableMics.map((d, i) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || t('players.deviceUnknown', { n: i + 1 })}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showCameraSelector && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium t-text-secondary block" htmlFor="setup-cam-select">
                    {t('players.selectCamera')}
                  </label>
                  <select
                    id="setup-cam-select"
                    value={selectedCameraId ?? ''}
                    onChange={(e) => changeCameraDevice?.(e.target.value || null)}
                    className="w-full t-bg-input t-text t-border border rounded-lg px-3 py-2 text-sm t-focus-ring"
                  >
                    {availableCameras.map((d, i) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || t('players.deviceUnknown', { n: i + 1 })}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Audio processing — shown after camera/mic enabled */}
          {hasAV && (
            <div className="t-bg-input rounded-xl p-4 space-y-2.5">
              <p className="text-xs font-medium t-text-secondary">{t('players.audioProcessing')}</p>
              {krispSupported && (
                <SetupCheckboxRow
                  checked={krispEnabled ?? true}
                  onChange={toggleKrisp}
                  label={t('players.krispNoise')}
                />
              )}
              <SetupCheckboxRow
                checked={audioConstraints?.echoCancellation ?? true}
                onChange={() => changeAudioConstraints?.({ echoCancellation: !audioConstraints?.echoCancellation })}
                label={t('players.echoCancellation')}
              />
              <SetupCheckboxRow
                checked={audioConstraints?.noiseSuppression ?? true}
                onChange={() => changeAudioConstraints?.({ noiseSuppression: !audioConstraints?.noiseSuppression })}
                label={t('players.noiseSuppression')}
              />
              <SetupCheckboxRow
                checked={audioConstraints?.autoGainControl ?? true}
                onChange={() => changeAudioConstraints?.({ autoGainControl: !audioConstraints?.autoGainControl })}
                label={t('players.gainControl')}
              />
            </div>
          )}
        </div>

        {/* Action buttons — fixed at bottom */}
        <div className="px-6 pb-6 pt-4 t-border border-t space-y-3 shrink-0">
          <button
            type="button"
            onClick={handleJoin}
            disabled={!hasAV}
            className="w-full t-btn-primary font-semibold rounded-lg px-4 py-3 t-focus-ring disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {gameActive ? t('mediaSetup.rejoinGame') : t('mediaSetup.joinGame')}
          </button>
          <button
            type="button"
            onClick={handleJoinWithoutAudio}
            className="w-full text-sm t-text-muted hover:t-text text-center py-1.5 t-focus-ring rounded transition-colors"
          >
            {t('mediaSetup.joinWithoutAudio')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SetupCheckboxRow({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer">
      <ThemedCheckbox checked={checked} onChange={onChange} className="t-checkbox t-focus-ring shrink-0" />
      <span className="text-sm t-text-secondary">{label}</span>
    </label>
  );
}
